"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCombinedDateTimeString = exports.format24HToAmPm = exports.suffix = exports.getUnitPrefix = exports.formatTime = exports.formatDateTime = void 0;
const dayjs_1 = __importDefault(require("dayjs"));
const types_1 = require("../types");
const utils_1 = require("../utils");
const validation_1 = require("../validation");
/**
 * Formats a Day.js instance as a string using the specified format and language region.
 * @deprecated
 * @param {dayjs.Dayjs} input - The Day.js instance to format.
 * @param {DateTimeFormats} [format=DateTimeFormats.LLLL] - The format to use for formatting. Default is the LLL.
 * @param {LanguageRegionCodes} [languageRegionCode=LanguageRegionCodes.EN_US] - The language and region code to use for localization. Default is the EN_US.
 * @returns {string} The formatted date and time string.
 */
const formatDateTime = (input, format = types_1.DEFAULT_FORMAT, languageRegionCode = types_1.DEFAULT_LANGUAGE_REGION_CODE) => {
    if (input instanceof Date || typeof input === 'string') {
        input = dayjs_1.default.utc(input);
    }
    if (!input.isValid()) {
        throw new types_1.CustomError(types_1.errorCode.invalidDate, 'Invalid date');
    }
    return input.locale(languageRegionCode).format(format);
};
exports.formatDateTime = formatDateTime;
/**
 * Formats a time string to the 24-hour format.
 *
 * @param {string} time - The time string to format.
 * @returns {string} The formatted time string.
 */
function formatTime(time) {
    const checkedTime = (time || '').trim();
    const currentDate = (0, utils_1.formatDate)(new Date(), types_1.DateTimeFormats.YYYY_MM_DD);
    const combinedDate = `${currentDate}T${checkedTime}`;
    const isValidTime = (0, dayjs_1.default)(combinedDate).isValid();
    let formattedTime;
    if (isValidTime) {
        formattedTime = checkedTime;
    }
    else {
        throw new Error(`Time '${time}' is not valid. Try ${types_1.DateTimeFormats.H24_WITH_SECONDS} or ${types_1.DateTimeFormats.H24_WITH_MINUTES} format`);
    }
    return formattedTime;
}
exports.formatTime = formatTime;
/**
 * Returns the appropriate prefix for the given time unit based on the language.
 *
 * @param unit - The time unit.
 * @param language - The language code (e.g., 'en', 'es').
 * @returns - The prefix associated with the time unit in the specified language.
 */
const getUnitPrefix = (unit, labels) => {
    var _a, _b, _c, _d, _e, _f, _g;
    switch (unit) {
        case types_1.TimeUnit.YEAR:
            return (_a = labels.year) !== null && _a !== void 0 ? _a : 'y';
        case types_1.TimeUnit.MONTH:
            return (_b = labels.month) !== null && _b !== void 0 ? _b : 'm';
        case types_1.TimeUnit.WEEK:
            return (_c = labels.week) !== null && _c !== void 0 ? _c : 'w';
        case types_1.TimeUnit.DAY:
            return (_d = labels.day) !== null && _d !== void 0 ? _d : 'd';
        case types_1.TimeUnit.HOUR:
            return (_e = labels.hour) !== null && _e !== void 0 ? _e : 'h';
        case types_1.TimeUnit.MINUTE:
            return (_f = labels.minute) !== null && _f !== void 0 ? _f : 'm';
        case types_1.TimeUnit.SECOND:
            return (_g = labels.secound) !== null && _g !== void 0 ? _g : 's';
        default:
            return '';
    }
};
exports.getUnitPrefix = getUnitPrefix;
/**
 * Concatenates two values into a single string.
 * Commonly used to combine a numeric value with a unit suffix (e.g., "5d" for 5 days).
 *
 * @param {number | string} a - The first value (typically a number)
 * @param {number | string} b - The second value (typically a unit suffix)
 * @returns {string} The concatenated string of both values
 */
const suffix = (a, b) => {
    return `${a}${b}`;
};
exports.suffix = suffix;
/**
 * Formats a time string to the AM/PM format, considering time zone.
 *
 * @param {string} time - The time string to format.
 * @returns {string} The formatted time string.
 */
function format24HToAmPm(time) {
    const dateWithTime = dayjs_1.default.utc(`1970-01-01T${time}Z`);
    if (!(0, validation_1.isValidDate)(dateWithTime, types_1.DateTimeFormats.H12_WITH_MINUTES)) {
        throw new types_1.CustomError(types_1.errorCode.invalidFormat, `Invalid time format: "${time}". Expected "${types_1.DateTimeFormats.H12_WITH_MINUTES}".`);
    }
    return (0, utils_1.formatDate)(dateWithTime, types_1.DateTimeFormats.H12_AMPM);
}
exports.format24HToAmPm = format24HToAmPm;
/* Combines a date and time string into a single date-time string.
 * @param date {string} - The date string.
 * @param time {string} - The time string.
 * @param [format=YYYY-MM-DDTHH:mm:ss] {DateTimeFormats} - The format to use for formatting.
 * @returns {string} The combined date-time string.
 */
const getCombinedDateTimeString = (date, time, format = types_1.DateTimeFormats.YYYY_MM_DD_T_HH_MM_SS) => {
    if (!(0, validation_1.isValidDate)(date, types_1.DateTimeFormats.YYYY_MM_DD) || !(0, validation_1.isValidDate)(time, types_1.DateTimeFormats.H24_WITH_SECONDS)) {
        throw new types_1.CustomError(types_1.errorCode.invalidDate, 'Invalid date or time');
    }
    const dateString = dayjs_1.default.utc(date).format(types_1.DateTimeFormats.YYYY_MM_DD);
    return dayjs_1.default.utc(`${dateString}T${time}`).format(format);
};
exports.getCombinedDateTimeString = getCombinedDateTimeString;
//# sourceMappingURL=formatter.js.map